import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Breadcrumbs,
    Link,
    Grid,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Menu,
    MenuItem,
    LinearProgress,
    Tooltip
} from '@mui/material';
import {
    Folder as FolderIcon,
    InsertDriveFile as FileIcon,
    CreateNewFolder as NewFolderIcon,
    CloudUpload as UploadIcon,
    MoreVert as MoreVertIcon,
    ArrowBack as ArrowBackIcon,
    Home as HomeIcon,
    Download as DownloadIcon,
    DriveFileMove as MoveIcon,
    Visibility as VisibilityIcon,
    Close as CloseIcon,
    NavigateBefore as PrevIcon,
    NavigateNext as NextIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon
} from '@mui/icons-material';
import { listFiles, createFolder, uploadFile, getFileUrl, moveFile } from '../../services/docsService';
import swal from 'sweetalert';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';

// Configure pdfjs worker (No longer needed for iframe view, but kept if needed)
// pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export default function Documentacion() {
    const [t] = useTranslation("global");

    // Check if user is admin
    const isAdmin = localStorage.getItem('rol') === 'administrador';

    // Helper to get embed URL (same as PDFPreviewDialog)
    const getEmbedUrl = (url) => {
        if (!url) return '';
        if (url.includes('storage.googleapis.com')) return url;
        if (url.includes('/preview')) return url;

        let fileId = null;
        const viewMatch = url.match(/\/file\/d\/([^\/]+)/);
        if (viewMatch) fileId = viewMatch[1];

        const openMatch = url.match(/[?&]id=([^&]+)/);
        if (openMatch) fileId = openMatch[1];

        if (fileId) return `https://drive.google.com/file/d/${fileId}/preview`;

        return url;
    };

    const theme = useTheme();
    const [currentPath, setCurrentPath] = useState("");
    const [items, setItems] = useState({ carpetas: [], archivos: [] });
    const [loading, setLoading] = useState(true);

    // Modals
    const [openNewFolder, setOpenNewFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState("");

    const [openUpload, setOpenUpload] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    // Context Menu
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);

    // Move Modal
    const [openMove, setOpenMove] = useState(false);
    const [moveDestination, setMoveDestination] = useState("");

    // Preview
    const [openPreview, setOpenPreview] = useState(false);
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewType, setPreviewType] = useState("");
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [scale, setScale] = useState(1.0);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setPageNumber(1);
    };

    // Cleanup object URLs to avoid memory leaks
    useEffect(() => {
        return () => {
            if (previewUrl && previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);


    const [rootPath, setRootPath] = useState(null); // base path for user

    useEffect(() => {
        const initUserFolder = async () => {
            const proveedorId = localStorage.getItem('Proveedor'); // e.g., "2000000210"

            if (proveedorId) {
                const userRoot = proveedorId + "/";
                setRootPath(userRoot);

                // Check if folder exists, if not create it
                // We'll just try to list it. If empty or error, we assume we might need to create it.
                // Or better, just try to create it. If it exists, backend might ignore or we catch error.
                // Let's try to list root first to see if it exists.
                try {
                    const data = await listFiles(userRoot);
                    if (!data) {
                        // If null, maybe doesn't exist? Try create.
                        await createFolder(userRoot);
                    }
                } catch (e) {
                    // If error, try create
                    try { await createFolder(userRoot); } catch (ex) { }
                }

                setCurrentPath(userRoot);
            } else {
                // Fallback for admin or testing if no cookie?
                setRootPath("");
                setCurrentPath("");
            }
        };
        initUserFolder();
    }, []);

    useEffect(() => {
        if (rootPath !== null && currentPath) {
            loadContent(currentPath);
        } else if (rootPath === "" && currentPath === "") {
            loadContent("");
        }
    }, [currentPath, rootPath]);

    const loadContent = async (path) => {
        setLoading(true);
        try {
            const data = await listFiles(path);
            setItems(data || { carpetas: [], archivos: [] });
        } catch (error) {
            console.error(error);
            swal(t("documentacion_content.error_cargar"), t("documentacion_content.error_cargar"), "error");
        } finally {
            setLoading(false);
        }
    };

    const handleNavigate = (path) => {
        // Ensure we don't double slash
        let normalizedPath = path.replace(/\/+/g, '/');

        // Security check: must start with rootPath
        if (rootPath && !normalizedPath.startsWith(rootPath)) {
            normalizedPath = rootPath;
        }

        setCurrentPath(normalizedPath);
    };

    // --- Move Logic & Folder Picker ---
    const [movePath, setMovePath] = useState("");
    const [moveFolders, setMoveFolders] = useState([]);
    const [loadingMove, setLoadingMove] = useState(false);

    const loadMoveFolders = async (path) => {
        setLoadingMove(true);
        try {
            const data = await listFiles(path);
            // Filter only folders and normalize
            const folders = (data?.carpetas || []).map(f => {
                const name = typeof f === 'string' ? f : f.name;
                return { name: name, displayName: name.replace(/\/$/, '').split('/').pop() };
            });
            setMoveFolders(folders);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingMove(false);
        }
    };

    const getDisplayName = (path) => {
        if (!path) return "";
        // Use regex for both slash types and filter empty parts
        const parts = path.split(/[\/\\]/).filter(Boolean);
        return parts.pop() || path;
    };

    const handleBreadcrumbClick = (index) => {
        // breadcrumbs are relative to rootPath
        const subPath = breadcrumbs.slice(0, index + 1).join("/") + "/";
        const newPath = (rootPath || "") + subPath;
        setCurrentPath(newPath);
    };

    // --- Create Folder ---
    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) return;
        try {
            const fullPath = currentPath + newFolderName.trim() + "/";
            await createFolder(fullPath);
            swal(t("documentacion_content.exito"), t("documentacion_content.exito_carpeta"), "success");
            setOpenNewFolder(false);
            setNewFolderName("");
            loadContent(currentPath);
        } catch (error) {
            swal(t("documentacion_content.error"), t("documentacion_content.error_carpeta"), "error");
        }
    };

    // --- Upload File ---
    const handleUpload = async () => {
        if (!selectedFile) return;
        setUploading(true);
        try {
            await uploadFile(currentPath, selectedFile);
            swal(t("documentacion_content.exito"), t("documentacion_content.exito_subida"), "success");
            setOpenUpload(false);
            setSelectedFile(null);
            loadContent(currentPath);
        } catch (error) {
            swal(t("documentacion_content.error"), t("documentacion_content.error_subida"), "error");
        } finally {
            setUploading(false);
        }
    };

    // --- File Actions ---
    const handleMenuOpen = (event, item) => {
        setAnchorEl(event.currentTarget);
        setSelectedItem(item);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedItem(null);
    };

    const simplifyUrl = (url) => {
        if (!url) return "";
        try {
            return url.split('?')[0];
        } catch (e) {
            return url;
        }
    };

    const handleDownload = async () => {
        const item = selectedItem; // Capture item before closing menu
        setAnchorEl(null); // Just close menu UI
        if (!item) return;
        try {
            const fullPath = item.name.startsWith(currentPath) ? item.name : currentPath + item.name;
            const res = await getFileUrl(fullPath);
            if (res.url) {
                // Use full URL for download/open in new tab to preserve signatures
                window.open(res.url, '_blank');
            } else {
                swal(t("documentacion_content.error"), t("documentacion_content.error_enlace"), "error");
            }
        } catch (error) {
            swal(t("documentacion_content.error"), t("documentacion_content.error_descarga"), "error");
        } finally {
            setSelectedItem(null); // Clear selection after action
        }
    };

    const handleMoveClick = () => {
        setMovePath(rootPath || "");
        loadMoveFolders(rootPath || "");
        setOpenMove(true);
        setAnchorEl(null); // Only close menu visual, keep selectedItem
    };

    const handleEnterMoveFolder = (folderName) => {
        let newPath = "";
        // If folderName already includes movePath and is more specific, it's a full path
        if (folderName.startsWith(movePath) && folderName.length > movePath.length) {
            newPath = folderName + (folderName.endsWith('/') ? '' : '/');
        } else {
            // Otherwise, it's likely a relative name or we need to append
            newPath = movePath + folderName.replace(/^\/+/, '') + (folderName.endsWith('/') ? '' : '/');
        }
        // Normalize double slashes
        newPath = newPath.replace(/\/+/g, '/');
        setMovePath(newPath);
        loadMoveFolders(newPath);
    };

    const handleUpMoveFolder = () => {
        if (!movePath || movePath === rootPath) return; // Already at root
        // Remove last folder
        const newPath = movePath.substring(0, movePath.lastIndexOf('/', movePath.length - 2) + 1);
        setMovePath(newPath);
        loadMoveFolders(newPath);
    };

    const handleMoveConfirm = async () => {
        if (!selectedItem) return;
        try {
            const rawName = selectedItem.name || "";
            // Source: If name starts with currentPath, it's full path. Else prepend.
            const source = rawName.startsWith(currentPath) ? rawName : currentPath + rawName;

            // Destination: We need the BASENAME of the file.
            const fileName = getDisplayName(rawName);
            const destination = movePath + fileName;

            await moveFile(source, destination);
            swal(t("documentacion_content.exito"), t("documentacion_content.exito_mover"), "success");
            setOpenMove(false);
            setSelectedItem(null); // Clear item after success
            loadContent(currentPath);
        } catch (error) {
            console.error(error);
            swal(t("documentacion_content.error"), t("documentacion_content.error_mover"), "error");
        }
    };

    const handlePreviewFile = async (item = selectedItem) => {
        if (!item) return;
        setAnchorEl(null);
        try {
            const fullPath = item.name.startsWith(currentPath) ? item.name : currentPath + item.name;
            const res = await getFileUrl(fullPath);
            if (res.url) {
                // Use the full URL directly. Do NOT simplify it, as it may contain signed params.
                // Do NOT fetch as blob client-side, let the iframe/browser handle it.
                setPreviewUrl(res.url);

                // Determine type
                if (item.contentType?.includes('pdf') || item.name.toLowerCase().endsWith('.pdf')) {
                    setPreviewType('application/pdf');
                } else {
                    setPreviewType(item.contentType || "");
                }

                setOpenPreview(true);
            } else {
                swal(t("documentacion_content.error"), t("documentacion_content.error_enlace"), "error");
            }
        } catch (error) {
            console.error("Error previewing file:", error);
            swal(t("documentacion_content.error"), t("documentacion_content.error_vista"), "error");
        }
    };

    // --- Render Helpers ---
    // Breadcrumbs: remove rootPath from display
    const relativePath = (currentPath && rootPath && currentPath.startsWith(rootPath))
        ? currentPath.substring(rootPath.length)
        : currentPath;

    const breadcrumbs = relativePath.split('/').filter(x => x);

    const renderIcon = (type, mime) => {
        if (type === 'folder') return <FolderIcon sx={{ fontSize: 40, color: '#fbc02d' }} />;
        if (mime?.includes('pdf')) return <FileIcon sx={{ fontSize: 40, color: '#f44336' }} />;
        if (mime?.includes('image')) return <FileIcon sx={{ fontSize: 40, color: '#4caf50' }} />;
        return <FileIcon sx={{ fontSize: 40, color: '#90caf9' }} />;
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
            {/* Header Actions */}
            <Paper sx={{ p: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {currentPath && currentPath !== rootPath && (
                        <IconButton onClick={() => setCurrentPath(currentPath.substring(0, currentPath.lastIndexOf('/', currentPath.length - 2) + 1))} sx={{ mr: 1 }}>
                            <ArrowBackIcon />
                        </IconButton>
                    )}
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link
                            underline="hover"
                            color="inherit"
                            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                            onClick={() => handleNavigate(rootPath || "")}
                        >
                            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                            {t("documentacion_content.inicio")}
                        </Link>
                        {breadcrumbs.map((value, index) => (
                            <Link
                                key={index}
                                underline="hover"
                                color="inherit"
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleBreadcrumbClick(index)}
                            >
                                {value}
                            </Link>
                        ))}
                    </Breadcrumbs>
                </Box>
                <Box>
                    <Button
                        variant="outlined"
                        startIcon={<NewFolderIcon />}
                        onClick={() => setOpenNewFolder(true)}
                        sx={{ mr: 2, borderRadius: '20px' }}
                    >
                        {t("documentacion_content.nueva_carpeta")}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<UploadIcon />}
                        onClick={() => setOpenUpload(true)}
                        sx={{ borderRadius: '20px' }}
                    >
                        {t("documentacion_content.subir_archivo")}
                    </Button>
                </Box>
            </Paper >

            {/* Content Area */}
            {
                loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {/* Folders */}
                        {items.carpetas.map((folderRaw, index) => {
                            const folderName = typeof folderRaw === 'string' ? folderRaw : folderRaw.name || t("documentacion_content.sin_nombre");
                            const displayName = getDisplayName(folderName);

                            return (
                                <Grid item xs={6} sm={4} md={3} lg={2} key={`folder-${index}`}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            border: '1px solid #eee',
                                            borderRadius: 3,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            '&:hover': { bgcolor: '#f5f5f5', transform: 'translateY(-2px)' },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            position: 'relative'
                                        }}
                                        onClick={() => {
                                            // Smart Navigate: If folderName is full path, use it. Else prepend.
                                            let targetPath = "";
                                            if (folderName.startsWith(currentPath)) {
                                                targetPath = folderName + (folderName.endsWith('/') ? '' : '/');
                                            } else {
                                                targetPath = currentPath + folderName + (folderName.endsWith('/') ? '' : '/');
                                            }
                                            handleNavigate(targetPath);
                                        }}
                                    >
                                        {renderIcon('folder')}
                                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, textAlign: 'center', wordBreak: 'break-word' }}>
                                            {displayName}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            );
                        })}

                        {/* Files */}
                        {items.archivos.map((file, index) => {
                            const displayName = getDisplayName(file.name);
                            return (
                                <Grid item xs={6} sm={4} md={3} lg={2} key={`file-${index}`}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            border: '1px solid #eee',
                                            borderRadius: 3,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            '&:hover': { bgcolor: '#f5f5f5', transform: 'translateY(-2px)' },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            position: 'relative'
                                        }}
                                        onClick={() => handlePreviewFile(file)}
                                    >
                                        <IconButton
                                            size="small"
                                            sx={{ position: 'absolute', top: 5, right: 5 }}
                                            onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, file); }}
                                        >
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                        <Box>
                                            {renderIcon('file', file.contentType)}
                                        </Box>
                                        <Tooltip title={file.name}>
                                            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', wordBreak: 'break-word', fontSize: '0.85rem', fontWeight: 600, color: 'text.primary' }}>
                                                {displayName.length > 30 ? displayName.substring(0, 27) + '...' : displayName}
                                            </Typography>
                                        </Tooltip>
                                        <Typography variant="caption" color="text.secondary">
                                            {(file.size / 1024).toFixed(1)} KB
                                        </Typography>
                                    </Paper>
                                </Grid>
                            );
                        })}

                        {items.carpetas.length === 0 && items.archivos.length === 0 && (
                            <Box sx={{ width: '100%', textAlign: 'center', mt: 5, color: 'text.secondary' }}>
                                <FolderIcon sx={{ fontSize: 60, opacity: 0.2 }} />
                                <Typography>{t("documentacion_content.carpeta_vacia")}</Typography>
                            </Box>
                        )}
                    </Grid>
                )
            }

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => handlePreviewFile()}>
                    <VisibilityIcon sx={{ mr: 1, fontSize: 20 }} /> {t("documentacion_content.ver")}
                </MenuItem>
                <MenuItem onClick={handleDownload}>
                    <DownloadIcon sx={{ mr: 1, fontSize: 20 }} /> {t("documentacion_content.descargar")}
                </MenuItem>
                {isAdmin && (
                    <MenuItem onClick={handleMoveClick}>
                        <MoveIcon sx={{ mr: 1, fontSize: 20 }} /> {t("documentacion_content.mover")}
                    </MenuItem>
                )}
            </Menu>

            {/* Create Folder Modal */}
            <Dialog open={openNewFolder} onClose={() => setOpenNewFolder(false)}>
                <DialogTitle>{t("documentacion_content.nueva_carpeta")}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t("documentacion_content.nombre_carpeta")}
                        fullWidth
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenNewFolder(false)}>{t("documentacion_content.cancelar")}</Button>
                    <Button onClick={handleCreateFolder} variant="contained">{t("documentacion_content.crear")}</Button>
                </DialogActions>
            </Dialog>

            {/* Upload Modal */}
            <Dialog open={openUpload} onClose={() => !uploading && setOpenUpload(false)}>
                <DialogTitle>{t("documentacion_content.subir_archivo")}</DialogTitle>
                <DialogContent sx={{ minWidth: 300 }}>
                    <Box sx={{ border: '2px dashed #ccc', p: 3, textAlign: 'center', borderRadius: 2, cursor: 'pointer' }}>
                        <input
                            type="file"
                            id="file-upload"
                            style={{ display: 'none' }}
                            onChange={(e) => setSelectedFile(e.target.files[0])}
                            disabled={uploading}
                        />
                        <label htmlFor="file-upload">
                            <Button component="span" disabled={uploading}>
                                {selectedFile ? selectedFile.name : t("documentacion_content.seleccionar_archivo")}
                            </Button>
                        </label>
                    </Box>
                    {uploading && <LinearProgress sx={{ mt: 2 }} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenUpload(false)} disabled={uploading}>{t("documentacion_content.cancelar")}</Button>
                    <Button onClick={handleUpload} variant="contained" disabled={!selectedFile || uploading}>
                        {uploading ? t("documentacion_content.subiendo") : t("documentacion_content.subir")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Move Modal */}
            {/* Move Modal (Folder Picker) */}
            <Dialog open={openMove} onClose={() => setOpenMove(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t("documentacion_content.mover_a")}</DialogTitle>
                <DialogContent dividers sx={{ height: 400, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                        {t("documentacion_content.destino")}: <strong>{movePath || t("documentacion_content.inicio")}</strong>
                    </Typography>

                    {movePath && (
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={handleUpMoveFolder}
                            sx={{ alignSelf: 'flex-start', mb: 1 }}
                        >
                            {t("documentacion_content.subir_nivel")}
                        </Button>
                    )}

                    <Box sx={{ flex: 1, overflowY: 'auto', border: '1px solid #eee', borderRadius: 2, p: 1 }}>
                        {loadingMove ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>
                        ) : (
                            <Grid container spacing={1}>
                                {moveFolders.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, width: '100%', textAlign: 'center' }}>
                                        {t("documentacion_content.no_hay_subcarpetas")}
                                    </Typography>
                                ) : (
                                    moveFolders.map((f, i) => {
                                        const fName = (typeof f === 'string' ? f : f.name).replace(/\/$/, '');
                                        return (
                                            <Grid item xs={12} key={i}>
                                                <Button
                                                    fullWidth
                                                    variant="text"
                                                    startIcon={<FolderIcon sx={{ color: '#fbc02d' }} />}
                                                    sx={{ justifyContent: 'flex-start', textTransform: 'none', color: 'text.primary' }}
                                                    onClick={() => handleEnterMoveFolder(typeof f === 'string' ? f : f.name)}
                                                >
                                                    {f.displayName}
                                                </Button>
                                            </Grid>
                                        )
                                    })
                                )}
                            </Grid>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenMove(false)}>{t("documentacion_content.cancelar")}</Button>
                    <Button onClick={handleMoveConfirm} variant="contained">
                        {t("documentacion_content.mover_aqui")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog
                open={openPreview}
                onClose={() => setOpenPreview(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { height: '90vh', bgcolor: '#333', display: 'flex', flexDirection: 'column' }
                }}
            >
                <DialogTitle sx={{ color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#222' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {t("documentacion_content.vista_previa")}
                        {numPages && (previewType.includes('pdf') || previewType === 'application/pdf') && (
                            <Typography variant="body2" sx={{ ml: 2, color: '#aaa' }}>
                                {t("documentacion_content.pagina")} {pageNumber} {t("documentacion_content.de")} {numPages}
                            </Typography>
                        )}
                    </Box>
                    <Box>
                        {(previewType.includes('pdf') || previewType === 'application/pdf') && (
                            <>
                                <IconButton onClick={() => setScale(s => Math.max(0.5, s - 0.1))} size="small" sx={{ color: '#fff', mr: 1 }}>
                                    <ZoomOutIcon />
                                </IconButton>
                                <IconButton onClick={() => setScale(s => Math.min(2.0, s + 0.1))} size="small" sx={{ color: '#fff', mr: 1 }}>
                                    <ZoomInIcon />
                                </IconButton>
                            </>
                        )}
                        <IconButton onClick={() => setOpenPreview(false)} sx={{ color: '#fff' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', bgcolor: '#444', flex: 1, position: 'relative' }}>
                    {(previewType.includes('image') || /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(previewUrl)) ? (
                        <img
                            src={previewUrl}
                            alt="Preview"
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                    ) : (previewType.includes('pdf') || previewUrl.toLowerCase().endsWith('.pdf') || previewType === 'application/pdf') ? (
                        <Box sx={{ width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
                            <iframe
                                src={getEmbedUrl(previewUrl)}
                                title="PDF Preview"
                                width="100%"
                                height="100%"
                                style={{
                                    border: 'none',
                                    flex: 1
                                }}
                            />
                        </Box>
                    ) : (
                        <Box sx={{ color: '#fff', textAlign: 'center' }}>
                            <Typography sx={{ mb: 2 }}>{t("documentacion_content.vista_no_disponible")}</Typography>
                            <Button
                                variant="contained"
                                onClick={() => window.open(previewUrl, '_blank')}
                                sx={{ mr: 1 }}
                            >
                                {t("documentacion_content.abrir_pestana")}
                            </Button>
                            <Button onClick={handleDownload} variant="outlined" sx={{ color: '#fff', borderColor: '#fff' }}>
                                {t("documentacion_content.descargar")}
                            </Button>
                        </Box>
                    )}
                </DialogContent>
                {(previewType.includes('pdf') || previewType === 'application/pdf') && numPages && (
                    <DialogActions sx={{ bgcolor: '#222', justifyContent: 'center', p: 1 }}>
                        <IconButton
                            disabled={pageNumber <= 1}
                            onClick={() => setPageNumber(p => p - 1)}
                            sx={{ color: '#fff' }}
                        >
                            <PrevIcon />
                        </IconButton>
                        <IconButton
                            disabled={pageNumber >= numPages}
                            onClick={() => setPageNumber(p => p + 1)}
                            sx={{ color: '#fff' }}
                        >
                            <NextIcon />
                        </IconButton>
                    </DialogActions>
                )}
            </Dialog>
        </Box >
    );
}
